/**
 * TransferReportsSuccessStep View Component
 *
 * Displays a green checkmark with a success message after the
 * transfer completes. The "Done" button closes the modal.
 * @module features/player-detail/components/TransferReportsSuccessStep
 */

import { CheckCircle } from "lucide-react-native";
import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";

/** Props for TransferReportsSuccessStepView */
export interface TransferReportsSuccessStepViewProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Callback fired when "Done" button is pressed */
  readonly onDone: () => void;
}

/**
 * Success screen showing a green checkmark and a "Done" button
 * @param root0 - Component props
 * @param root0.onDone - Done button handler
 */
const TransferReportsSuccessStepView = ({
  onDone,
}: TransferReportsSuccessStepViewProps) => (
  <Stack className="flex-1 items-center justify-center gap-6 p-6">
    <Box
      testID="transfer-success-icon"
      className="size-16 items-center justify-center rounded-full bg-status-success-surface"
    >
      <Icon as={CheckCircle} size="xl" className="text-status-success" />
    </Box>

    <Stack className="items-center gap-2">
      <Text variant="title-lg" className="text-center text-content-primary">
        Transfer Complete
      </Text>
      <Text variant="title-sm" className="text-center text-content-secondary">
        All reports and notes have been successfully transferred.
      </Text>
    </Stack>

    <Button
      label="Done"
      tone="positive"
      size="lg"
      labelTone="on-accent"
      onPress={onDone}
      className="w-full"
      testID="transfer-done-button"
    />
  </Stack>
);

TransferReportsSuccessStepView.displayName = "TransferReportsSuccessStepView";

export default memo(TransferReportsSuccessStepView);
